/***
 *
 *   useOverlay
 *   The behaviour Radix gave the reference's Dialog / Sheet for free, which we
 *   have to provide by hand (see docs/ui-refresh/PLAN.md — "no new deps").
 *
 *   Shared by Modal, Popup and Pannel so there is one implementation to get
 *   right rather than three to keep in sync:
 *
 *     - focus moves into the overlay on open and returns to whatever had it
 *       when the overlay closes
 *     - Tab and Shift+Tab cycle inside the overlay instead of escaping into
 *       the page behind it
 *     - Escape closes, unless focus is in a field that wants the key itself
 *     - the page behind stops scrolling while the overlay is open
 *
 *   USAGE
 *   const ref = useOverlay({ onClose, closeOnEscape: true });
 *   return <div ref={ref}>…</div>;
 *
 **********/

import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
}

export function useOverlay(options = {}) {
    const { onClose, closeOnEscape = true, autoFocus = true, lockScroll = true } = options;
    const containerRef = useRef(null);
    const restoreRef = useRef(null);

    // Captured in a ref rather than a dep so a caller passing an inline arrow
    // does not re-run the whole effect on every render.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const handleKeyDown = useCallback(
        (e) => {
            const container = containerRef.current;
            if (!container) return;

            if (e.key === 'Escape' && closeOnEscape) {
                // A select with an open native listbox, or a field mid-edit,
                // should consume Escape itself — the reference's detail panel
                // does exactly this check.
                const t = e.target;
                const tag = t?.tagName;
                if (
                    tag === 'INPUT' ||
                    tag === 'TEXTAREA' ||
                    tag === 'SELECT' ||
                    t?.isContentEditable
                ) {
                    return;
                }
                e.stopPropagation();
                onCloseRef.current?.();
                return;
            }

            if (e.key !== 'Tab') return;

            const items = focusable(container);
            if (!items.length) {
                e.preventDefault();
                return;
            }

            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;

            if (e.shiftKey && (active === first || !container.contains(active))) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        },
        [closeOnEscape]
    );

    useEffect(() => {
        restoreRef.current = document.activeElement;

        if (autoFocus) {
            // Defer a frame: children may still be mounting on the first pass.
            const id = requestAnimationFrame(() => {
                const items = focusable(containerRef.current);
                (items[0] || containerRef.current)?.focus?.();
            });
            var cancel = () => cancelAnimationFrame(id);
        }

        document.addEventListener('keydown', handleKeyDown, true);

        let previousOverflow;
        if (lockScroll) {
            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }

        return () => {
            cancel?.();
            document.removeEventListener('keydown', handleKeyDown, true);
            if (lockScroll) document.body.style.overflow = previousOverflow || '';

            // Only pull focus back if it is still inside the overlay — the
            // caller may have moved it deliberately on the way out.
            const restore = restoreRef.current;
            if (restore && typeof restore.focus === 'function') {
                const active = document.activeElement;
                if (!active || active === document.body || containerRef.current?.contains(active)) {
                    restore.focus();
                }
            }
        };
    }, [handleKeyDown, autoFocus, lockScroll]);

    return containerRef;
}
